import CryptoJS from 'crypto-js';
import {
    AuditV0EventPostRequest,
    AuditV0RuntimePostRequest,
    Channel,
    Configuration,
    Metrics,
    Type,
} from 'remotes/@api/audit_v0';
import { AuditV0ReviewPostRequest } from 'types/@api/audit_v0/apis/ReviewControllerApi';
import { v4 as uuidv4 } from 'uuid';

const { EventControllerApi, ReviewControllerApi, RuntimeControllerApi } =
    await import('remotes/@api/audit_v0');
const { globalStore } = await import('remotes/@global-store/GlobalStore');

/**
 * Review.
 */
export interface Review {
    /**
     * @description Оценка приложения 1-5.
     * @type {number}
     * @memberof Review1
     */
    rating: number;
    /**
     * @description Комментарий обратной связи.
     * @type {string}
     * @memberof Review1
     */
    comment?: string;
    /**
     * @description Почта для обратной связи.
     * @type {any}
     * @memberof Review1
     */
    email?: string;
}

/**
 * SuspenseLoader of data for global stores.
 */
export class AuditService {
    /**
     * @description Runtime.
     * @param {Review} review - Review.
     * @param {boolean} isAuthorizationProcessed - IsAuthorizationProcessed.
     */
    static async review(review: Review, isAuthorizationProcessed: boolean) {
        try {
            const id = uuidv4();
            const requestData: AuditV0ReviewPostRequest = {
                review1: {
                    channel: Channel.WEB,
                    version: String(localStorage.getItem('appVersion')),
                    platform: globalStore.deviseInfo.os,
                    ...review,
                },
            };
            const config = new Configuration({
                ...(!isAuthorizationProcessed
                    ? {
                          username: globalStore.clientId,
                          password: await generatePasswordWithRSA(
                              CryptoJS.MD5(globalStore.clientId).toString() +
                                  id,
                          ),
                      }
                    : {}),
            });
            const api = new ReviewControllerApi(config);
            await api.auditV0ReviewPost(requestData);
        } catch (e) {
            ///
        }
    }

    /**
     * @description Event.
     * @param {Array<Metrics>} metrics - Metrics.
     * @param {boolean} isAuthorizationProcessed - IsAuthorizationProcessed.
     */
    static async event(
        metrics: Array<Metrics>,
        isAuthorizationProcessed: boolean,
    ) {
        try {
            const id = uuidv4();

            const requestData: AuditV0EventPostRequest = {
                eventDataApiRequestDto: {
                    metrics,
                    channel: Channel.WEB,
                    version: String(localStorage.getItem('appVersion')),
                    device: {
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        latitude: globalStore.deviseInfo.coordinates.latitude
                            ? globalStore.deviseInfo.coordinates.latitude
                            : null,
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        longitude: globalStore.deviseInfo.coordinates.longitude
                            ? globalStore.deviseInfo.coordinates.longitude
                            : null,
                        timezone: globalStore.deviseInfo.coordinates.timeZone,
                        type: Type[
                            globalStore.deviseInfo.deviceType.toUpperCase()
                        ],
                        os: globalStore.deviseInfo.browser.version,
                        country: globalStore.deviseInfo.coordinates.country,
                        region: globalStore.deviseInfo.coordinates.region,
                        platform: globalStore.deviseInfo.os,
                        model: globalStore.deviseInfo.browser.name,
                    },
                    id,
                },
            };
            if (
                globalStore.deviceId &&
                globalStore.deviceId !== 'null' &&
                requestData?.eventDataApiRequestDto?.device
            ) {
                requestData.eventDataApiRequestDto.device.uid =
                    globalStore.deviceId;
            }
            const config = new Configuration({
                ...(!isAuthorizationProcessed
                    ? {
                          username: globalStore.clientId,
                          password: await generatePasswordWithRSA(
                              CryptoJS.MD5(globalStore.clientId).toString() +
                                  id,
                          ),
                      }
                    : {}),
            });

            const api = new EventControllerApi(config);
            await api.auditV0EventPost(requestData);
        } catch (e) {
            //
        }
    }

    /**
     * @description Event.
     * @param {Array<Metrics>} metrics - Metrics.
     * @param {boolean} isAuthorizationProcessed - IsAuthorizationProcessed.
     */
    static async runtime(
        metrics: Array<Metrics>,
        isAuthorizationProcessed: boolean,
    ) {
        try {
            const id = uuidv4();
            const requestData: AuditV0RuntimePostRequest = {
                eventRuntime: {
                    metrics,
                    version: String(localStorage.getItem('appVersion')),
                    deviceid: globalStore.deviceId,
                    timezone: globalStore.deviseInfo.coordinates.timeZone,
                    type: Type[globalStore.deviseInfo.deviceType.toUpperCase()],
                    os: globalStore.deviseInfo.browser.version,
                    platform: globalStore.deviseInfo.os,
                    model: globalStore.deviseInfo.browser.name,
                    id,
                },
            };

            const config = new Configuration({
                ...(!isAuthorizationProcessed
                    ? {
                          username: globalStore.clientId,
                          password: await generatePasswordWithRSA(
                              CryptoJS.MD5(globalStore.clientId).toString() +
                                  id,
                          ),
                      }
                    : {}),
            });
            const api = new RuntimeControllerApi(config);
            await api.auditV0RuntimePost(requestData);
        } catch (e) {
            ///
        }
    }
}

async function generatePasswordWithRSA(message: string): Promise<string> {
    const pemContents = String(process.env.REACT_APP_AUDIT_CERTIFICATE);
    const binaryDerString = window.atob(pemContents);
    const binaryDer = Uint8Array.from(
        binaryDerString.split('').map((letter) => letter.charCodeAt(0)),
    );
    try {
        const publicKey = await window.crypto.subtle.importKey(
            'spki',
            binaryDer,
            {
                name: 'RSA-OAEP',
                hash: { name: 'SHA-256' },
            },
            true,
            ['encrypt'],
        );
        const encryptData = await encryptDataWithPublicKey(message, publicKey);
        const rdata = arrayBufferToString(encryptData);
        return window.btoa(rdata);
    } catch (err) {
        // eslint-disable-next-line no-console
        console.log(err);
        return '';
    }
}

function arrayBufferToString(str) {
    const byteArray = new Uint8Array(str);
    let byteString = '';
    for (let i = 0; i < byteArray.byteLength; i += 1) {
        byteString += String.fromCodePoint(byteArray[i]);
    }
    return byteString;
}

function encryptDataWithPublicKey(data, key) {
    const encoder = new TextEncoder();

    return window.crypto.subtle.encrypt(
        {
            name: 'RSA-OAEP',
        },
        key,
        encoder.encode(data),
    );
}
